import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Button, Spinner, Form } from "react-bootstrap";
import api from "../../api";
import StatusChip from "../../components/common/StatusChip";
import "./PreventiveMaintenancePage.css";
import "./TechnicianDesignSystem.css";
import TechModalTitle from "./TechModalTitle";

// Inline stroke icons (currentColor) so this page never depends on an icon
// font loading -- every icon on the cards/KPIs below is one of these.
const ICON_PATHS = {
  clipboard: (
    <>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <line x1="9" y1="12" x2="15" y2="12" />
      <line x1="9" y1="16" x2="15" y2="16" />
    </>
  ),
  checkCircle: (
    <>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </>
  ),
  check: <polyline points="20 6 9 17 4 12" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </>
  ),
  alert: (
    <>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </>
  ),
  shield: (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </>
  ),
  timer: (
    <>
      <circle cx="12" cy="13" r="8" />
      <path d="M12 9v4l2 2" />
      <path d="M9 2h6" />
    </>
  ),
  history: (
    <>
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
      <polyline points="12 7 12 12 15 14" />
    </>
  ),
  flag: (
    <>
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" />
    </>
  ),
  paperclip: (
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  ),
};

function Icon({ name, size = 18 }) {
  return (
    <svg
      className="pm-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

// Card-level display state. The backend only knows PENDING / COMPLETED /
// MISSED; MISSED (a past month that was never confirmed) is what this page
// calls "Overdue". A still-PENDING check whose period has ended (page left
// open across a month rollover) is shown as Overdue too, purely as display.
const STATE_META = {
  pending: { label: "Pending", icon: "clock" },
  completed: { label: "Completed", icon: "check" },
  overdue: { label: "Overdue", icon: "alert" },
};

function StateBadge({ state }) {
  const meta = STATE_META[state];
  return (
    <span className={`pm-badge pm-badge-${state}`}>
      <Icon name={meta.icon} size={14} />
      {meta.label}
    </span>
  );
}

function todayIsoDate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function deriveCheckState(check, period) {
  if (check.status === "COMPLETED") return "completed";
  if (check.status === "MISSED") return "overdue";
  if (period?.end_date && todayIsoDate() > period.end_date) return "overdue";
  return "pending";
}

// Last `count` calendar months (current month first), independent of any
// data already on screen -- the technician must be able to pick the current
// month (always) and browse read-only history even before any row exists
// for it. Uses the browser's local calendar, matching this app's other
// month pickers (src/utils/monthFilter.js).
function buildRecentMonthOptions(count = 12) {
  const now = new Date();
  const options = [];
  for (let i = 0; i < count; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    options.push({ value, label });
  }
  return options;
}

function formatDateShort(isoDate) {
  if (!isoDate) return "—";
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
}

// "Sep 01 – Sep 30, 2026" -- compact period range for the metadata block,
// only showing the year once when both dates fall in it.
function formatPeriodRange(startIso, endIso) {
  if (!startIso || !endIso) return "—";
  const start = new Date(`${startIso}T00:00:00`);
  const end = new Date(`${endIso}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return `${startIso} – ${endIso}`;
  const startLabel = start.toLocaleDateString("en-US", { day: "2-digit", month: "short" });
  const endLabel = end.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  return `${startLabel} – ${endLabel}`;
}

function formatDateTime(isoDateTime) {
  if (!isoDateTime) return "—";
  const d = new Date(isoDateTime);
  if (Number.isNaN(d.getTime())) return isoDateTime;
  return d.toLocaleString("en-US", {
    day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
  });
}

// Client-side display derivation only (not a new business rule): the
// backend already returns each history row's per-check statuses; this just
// rolls them into the same completed/total percentage the current-month
// summary already computes, for the History table's Compliance column.
function historyRowCompliance(row) {
  if (!row?.checks?.length) return null;
  const completed = row.checks.filter((c) => c.status === "COMPLETED").length;
  return Math.round((completed / row.checks.length) * 100);
}

function MetaItem({ icon, label, value }) {
  return (
    <div className="pm-meta-item">
      <span className="pm-meta-label">
        <Icon name={icon} size={14} />
        {label}
      </span>
      <span className="pm-meta-value">{value}</span>
    </div>
  );
}

function StatCard({ tone, icon, label, value, caption, children }) {
  return (
    <div className={`pm-stat-card pm-stat-${tone}${parseInt(value, 10) === 0 ? " pm-stat-zero" : ""}`}>
      <div className="pm-stat-head">
        <span className="pm-stat-icon"><Icon name={icon} size={20} /></span>
        <span className="pm-stat-label">{label}</span>
      </div>
      <strong className="pm-stat-value">{value}</strong>
      {children || <span className="pm-stat-caption">{caption}</span>}
    </div>
  );
}

export default function PreventiveMaintenancePage() {
  const monthOptions = useMemo(() => buildRecentMonthOptions(12), []);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0]?.value);

  const [activeTab, setActiveTab] = useState("current");

  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  const [confirmTarget, setConfirmTarget] = useState(null);
  const [workPerformed, setWorkPerformed] = useState("");
  const [remarks, setRemarks] = useState("");
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [workPerformedError, setWorkPerformedError] = useState(null);

  // Shared "View Details" modal -- used by both the current-month completed
  // card and the History table, so a completed check's full, untruncated
  // Work Performed / Remarks / Evidence are always shown the same way.
  const [detailsTarget, setDetailsTarget] = useState(null);

  const fetchStatus = useCallback(async (month) => {
    setLoading(true);
    setError(null);
    setErrorCode(null);
    try {
      const { data } = await api.get("/technician/preventive-maintenance", { params: { month } });
      setStatusData(data);
    } catch (err) {
      setErrorCode(err?.response?.data?.code || null);
      setError(err?.response?.data?.message || "Failed to load preventive maintenance status. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedMonth) fetchStatus(selectedMonth);
  }, [selectedMonth, fetchStatus]);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const { data } = await api.get("/technician/preventive-maintenance/history", { params: { months: 6 } });
      setHistoryData(data.history || []);
    } catch (err) {
      setHistoryError(err?.response?.data?.message || "Failed to load history. Please try again.");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "history" && historyData === null && !historyLoading) {
      fetchHistory();
    }
  }, [activeTab, historyData, historyLoading, fetchHistory]);

  const openConfirmModal = (check) => {
    setConfirmTarget(check);
    setWorkPerformed("");
    setRemarks("");
    setEvidenceFile(null);
    setSubmitError(null);
    setWorkPerformedError(null);
  };

  const closeConfirmModal = () => {
    if (submitting) return;
    setConfirmTarget(null);
  };

  const handleConfirm = async () => {
    if (!confirmTarget) return;

    const trimmedWorkPerformed = workPerformed.trim();
    if (!trimmedWorkPerformed) {
      setWorkPerformedError("Please describe the work performed before confirming.");
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setWorkPerformedError(null);
    try {
      const { data } = await api.post(
        `/technician/preventive-maintenance/${confirmTarget.sub_category_id}/confirm`,
        { month: selectedMonth, work_performed: trimmedWorkPerformed, remarks: remarks.trim() || undefined }
      );
      setStatusData(data);
      setConfirmTarget(null);

      // Evidence is a separate, best-effort request AFTER the confirmation
      // has already succeeded (matches this codebase's own "create parent
      // record, then attach a file" convention) -- a failure here must
      // never look like the confirmation itself failed.
      if (evidenceFile) {
        const confirmedCheck = (data.checks || []).find((c) => c.sub_category_id === confirmTarget.sub_category_id);
        if (confirmedCheck?.id) {
          try {
            const formData = new FormData();
            formData.append("evidence", evidenceFile);
            await api.post(`/technician/preventive-maintenance/checks/${confirmedCheck.id}/evidence`, formData);
            fetchStatus(selectedMonth);
          } catch (evidenceErr) {
            console.error("Evidence upload failed:", evidenceErr?.response?.data?.message || evidenceErr.message);
          }
        }
      }
    } catch (err) {
      const code = err?.response?.data?.code;
      setSubmitError(err?.response?.data?.message || "Failed to confirm. Please try again.");
      if (code === "PREVENTIVE_CHECK_ALREADY_CONFIRMED") {
        fetchStatus(selectedMonth);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openDetails = (check, monthLabel) => {
    setDetailsTarget({ ...check, monthLabel });
  };

  const closeDetails = () => setDetailsTarget(null);

  const viewEvidence = async (checkId) => {
    try {
      const res = await api.get(`/technician/preventive-maintenance/checks/${checkId}/evidence`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Failed to open evidence:", err?.response?.data?.message || err.message);
    }
  };

  const historyColumns = historyData && historyData.length ? historyData[0].checks.map((c) => c.name) : [];
  const selectedMonthLabel = monthOptions.find((o) => o.value === selectedMonth)?.label;

  // KPI figures come from the same payload the cards render, so completing a
  // check (which replaces statusData with the server's fresh response)
  // updates Completed / Pending / Overdue / Compliance and the bar together.
  const kpis = useMemo(() => {
    if (!statusData) return null;
    const checks = statusData.checks || [];
    const summary = statusData.summary || {};
    const required = summary.required ?? checks.length;
    const completed = summary.completed ?? checks.filter((c) => c.status === "COMPLETED").length;
    const overdue = checks.filter((c) => deriveCheckState(c, statusData.period) === "overdue").length;
    const pending = Math.max(required - completed - overdue, 0);
    const compliance = summary.compliance_percent ?? (required ? Math.round((completed / required) * 100) : 0);
    return { required, completed, pending, overdue, compliance };
  }, [statusData]);

  return (
    <div className="pm-page tech-ds">
      <div className="pm-header tech-page-header">
        <div className="pm-header-left">
          <div className="pm-eyebrow tech-section-label">Technician</div>
          <h2 className="pm-title tech-page-title">Preventive Maintenance</h2>
          <p className="pm-subtitle tech-page-subtitle">Monthly Antivirus &amp; Patch Compliance</p>
        </div>
        <div className="pm-header-right tech-header-actions">
          <label className="pm-month-label" htmlFor="pm-month-select">Month</label>
          <Form.Select
            id="pm-month-select"
            className="pm-month-select"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            aria-label="Select month"
          >
            {monthOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Select>
        </div>
      </div>

      <div className="pm-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "current"}
          className={`pm-tab ${activeTab === "current" ? "pm-tab-active" : ""}`}
          onClick={() => setActiveTab("current")}
        >
          Current Month
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "history"}
          className={`pm-tab ${activeTab === "history" ? "pm-tab-active" : ""}`}
          onClick={() => setActiveTab("history")}
        >
          History
        </button>
      </div>

      {activeTab === "current" && (
        <>
          {loading && (
            <div className="unit-loader pm-loader">
              <Spinner animation="border" />
              <p>Loading preventive maintenance status…</p>
            </div>
          )}

          {!loading && error && (
            <div className="unit-empty pm-error">
              <span className="pm-error-icon"><Icon name="alert" size={28} /></span>
              <h5>{errorCode === "PREVENTIVE_CONFIG_MISSING" ? "Not configured yet" : "Something went wrong"}</h5>
              <p>{error}</p>
              {errorCode !== "PREVENTIVE_CONFIG_MISSING" && (
                <Button className="unit-btn unit-btn-primary" onClick={() => fetchStatus(selectedMonth)}>
                  Retry
                </Button>
              )}
            </div>
          )}

          {!loading && !error && statusData && kpis && (
            <>
              <div className="pm-stats">
                <StatCard tone="required" icon="clipboard" label="Required Checks" value={kpis.required} caption="Total this period" />
                <StatCard tone="completed" icon="checkCircle" label="Completed" value={kpis.completed} caption="Confirmed this period" />
                <StatCard tone="pending" icon="clock" label="Pending" value={kpis.pending} caption="Awaiting action" />
                <StatCard tone="overdue" icon="alert" label="Overdue" value={kpis.overdue} caption="Past due date" />
                <StatCard tone="compliance" icon="shield" label="Compliance" value={`${kpis.compliance}%`}>
                  <div
                    className="pm-progress"
                    role="progressbar"
                    aria-label="Compliance"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={kpis.compliance}
                  >
                    <div className="pm-progress-fill" style={{ width: `${kpis.compliance}%` }} />
                  </div>
                </StatCard>
              </div>

              <div className="pm-section-heading">
                <h3>Monthly Checks</h3>
                <p>Complete each required preventive activity once during this month.</p>
              </div>

              <div className="pm-check-list">
                {statusData.checks.map((check) => {
                  const state = deriveCheckState(check, statusData.period);
                  return (
                    <div className={`pm-check-card pm-check-card-${state}`} key={check.sub_category_id}>
                      <div className="pm-check-card-header">
                        <h3>{check.name}</h3>
                        <StateBadge state={state} />
                      </div>
                      <p className="pm-check-subtitle">Monthly preventive security check</p>

                      <div className="pm-check-meta">
                        <MetaItem
                          icon="calendar"
                          label="Period"
                          value={formatPeriodRange(statusData.period.start_date, statusData.period.end_date)}
                        />
                        <MetaItem icon="timer" label="TAT" value={`${check.tat_days} Days`} />
                        <MetaItem
                          icon="history"
                          label="Last Completed"
                          value={check.status === "COMPLETED" ? formatDateShort(String(check.confirmed_at || "").slice(0, 10)) : "—"}
                        />
                        <MetaItem icon="flag" label="Due Date" value={formatDateShort(statusData.period.end_date)} />
                      </div>

                      {state === "completed" && (
                        <div className="pm-check-completed">
                          <MetaItem icon="checkCircle" label="Completed on" value={formatDateTime(check.confirmed_at)} />
                          {check.confirmed_by && (
                            <MetaItem icon="user" label="Completed by" value={check.confirmed_by} />
                          )}
                          {check.work_performed && (
                            <div className="pm-meta-item pm-meta-wide">
                              <span className="pm-meta-label">Work performed</span>
                              <span className="pm-meta-value pm-clamp-2">{check.work_performed}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {state === "overdue" && (
                        <p className="pm-check-overdue-note">
                          {check.status === "MISSED"
                            ? "No confirmation was recorded for this period. Past months are read-only."
                            : "This check is past its due date. Please complete it as soon as possible."}
                        </p>
                      )}

                      <div className="pm-check-footer">
                        {check.status === "PENDING" && (
                          <Button
                            className="unit-btn unit-btn-primary pm-confirm-btn"
                            onClick={() => openConfirmModal(check)}
                          >
                            <Icon name="check" size={16} />
                            Mark as Completed
                          </Button>
                        )}
                        {check.status === "COMPLETED" && (
                          <>
                            <Button className="pm-done-btn" disabled>
                              <Icon name="check" size={16} />
                              Completed
                            </Button>
                            <Button
                              className="unit-btn unit-btn-light pm-view-btn"
                              onClick={() => openDetails(check, selectedMonthLabel)}
                            >
                              View Details
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}

      {activeTab === "history" && (
        <div className="pm-history">
          <div className="pm-section-heading">
            <h3>Preventive Maintenance History</h3>
          </div>

          {historyLoading && (
            <div className="unit-loader pm-loader">
              <Spinner animation="border" />
              <p>Loading history…</p>
            </div>
          )}

          {!historyLoading && historyError && (
            <div className="unit-empty pm-error">
              <span className="pm-error-icon"><Icon name="alert" size={28} /></span>
              <h5>Something went wrong</h5>
              <p>{historyError}</p>
              <Button className="unit-btn unit-btn-primary" onClick={fetchHistory}>Retry</Button>
            </div>
          )}

          {!historyLoading && !historyError && historyData && historyData.length === 0 && (
            <div className="unit-empty">
              <span className="pm-error-icon"><Icon name="history" size={28} /></span>
              <h5>No history yet</h5>
              <p>No preventive-maintenance history available yet.</p>
            </div>
          )}

          {!historyLoading && !historyError && historyData && historyData.length > 0 && (
            <div className="unit-card pm-history-card">
              <div className="unit-table-wrapper pm-table-wrap">
                <table className="pm-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      {historyColumns.map((name) => <th key={name}>{name}</th>)}
                      <th>Overall</th>
                      <th>Compliance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.map((row) => (
                      <tr key={row.month}>
                        <td className="pm-table-month">{row.label}</td>
                        {row.checks.map((c) => (
                          <td key={c.sub_category_id}>
                            {c.status === "COMPLETED" ? (
                              <button
                                type="button"
                                className="pm-history-chip-btn"
                                onClick={() => openDetails(c, row.label)}
                              >
                                <StatusChip status={c.status} />
                              </button>
                            ) : (
                              <StatusChip status={c.status} />
                            )}
                            {c.status === "COMPLETED" && c.confirmed_at && (
                              <div className="pm-history-date">{formatDateShort(c.confirmed_at.slice(0, 10))}</div>
                            )}
                          </td>
                        ))}
                        <td><StatusChip status={row.overall} /></td>
                        <td className="pm-table-compliance">{historyRowCompliance(row)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <Modal show={!!confirmTarget} onHide={closeConfirmModal} centered className="unit-modal pm-modal tech-modal">
        <Modal.Header closeButton={!submitting} closeLabel="Close">
          <TechModalTitle icon="fa-screwdriver-wrench">Confirm Preventive Maintenance</TechModalTitle>
        </Modal.Header>
        <Modal.Body>
          <div className="pm-modal-summary">
            <div className="pm-meta-item">
              <span className="pm-meta-label">Check</span>
              <span className="pm-meta-value">{confirmTarget?.name}</span>
            </div>
            <div className="pm-meta-item">
              <span className="pm-meta-label">Month</span>
              <span className="pm-meta-value">{selectedMonthLabel}</span>
            </div>
          </div>
          <Form.Group className="mb-3" controlId="pm-completion-date">
            <Form.Label>Completion Date</Form.Label>
            <Form.Control type="text" readOnly value={formatDateShort(todayIsoDate())} />
            <Form.Text muted>Recorded automatically when you confirm.</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3" controlId="pm-work-performed">
            <Form.Label>Work Performed <span className="pm-required">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={2000}
              value={workPerformed}
              onChange={(e) => {
                setWorkPerformed(e.target.value);
                if (workPerformedError) setWorkPerformedError(null);
              }}
              placeholder="e.g. Updated antivirus definitions to latest version and completed full-system scan."
              disabled={submitting}
              isInvalid={!!workPerformedError}
            />
            {workPerformedError && <Form.Control.Feedback type="invalid">{workPerformedError}</Form.Control.Feedback>}
          </Form.Group>
          <Form.Group className="mb-3" controlId="pm-remarks">
            <Form.Label>Remarks (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              maxLength={500}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Any additional notes."
              disabled={submitting}
            />
          </Form.Group>
          <Form.Group className="mb-1" controlId="pm-evidence">
            <Form.Label>Evidence / Attachment (optional)</Form.Label>
            <Form.Control
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt"
              onChange={(e) => setEvidenceFile(e.target.files?.[0] || null)}
              disabled={submitting}
            />
          </Form.Group>
          {submitError && <p className="pm-modal-error">{submitError}</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={closeConfirmModal} disabled={submitting}>
            Cancel
          </Button>
          <Button className="unit-btn unit-btn-primary" onClick={handleConfirm} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Confirming…
              </>
            ) : (
              "Confirm Completion"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!detailsTarget} onHide={closeDetails} centered className="unit-modal pm-modal tech-modal">
        <Modal.Header closeButton>
          <TechModalTitle icon="fa-circle-info">{detailsTarget?.name}</TechModalTitle>
        </Modal.Header>
        <Modal.Body>
          <div className="pm-details-grid">
            <div className="pm-meta-item">
              <span className="pm-meta-label">Month</span>
              <span className="pm-meta-value">{detailsTarget?.monthLabel}</span>
            </div>
            <div className="pm-meta-item">
              <span className="pm-meta-label">Completed by</span>
              <span className="pm-meta-value">{detailsTarget?.confirmed_by || "—"}</span>
            </div>
            <div className="pm-meta-item">
              <span className="pm-meta-label">Completed on</span>
              <span className="pm-meta-value">{formatDateTime(detailsTarget?.confirmed_at)}</span>
            </div>
          </div>
          <div className="pm-details-block">
            <span className="pm-meta-label">Work Performed</span>
            <p className="pm-details-text">{detailsTarget?.work_performed || "—"}</p>
          </div>
          <div className="pm-details-block">
            <span className="pm-meta-label">Remarks</span>
            <p className="pm-details-text">{detailsTarget?.remarks || "—"}</p>
          </div>
          <div className="pm-details-block">
            <span className="pm-meta-label">Evidence</span>
            {detailsTarget?.evidence ? (
              <Button
                className="unit-btn unit-btn-light pm-evidence-btn"
                onClick={() => viewEvidence(detailsTarget.id)}
              >
                <Icon name="paperclip" size={16} />
                {detailsTarget.evidence.original_name || "View Evidence"}
              </Button>
            ) : (
              <p className="pm-details-text">No evidence uploaded.</p>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={closeDetails}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
